/**
 * String manipulation.
 */

import { isNumber, isFunc, isString } from './js';

/**
 * Build a URL with a query string.
 *
 * @param {string} url - Base URL.
 * @param {object} params - Query parameters.
 * @returns {string}
 * @example
 *
 * buildUrl('https://example.com', { param: 'value', other: 2 });
 * // => 'https://example.com?param=value&other=2'
 */
export function buildUrl(url, params) {
  const queryString = Object.keys(params || {})
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');

  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Build a string from parts, filtering out falsy values other than zero.
 *
 * @param {Array} parts - Parts to join.
 * @returns {string}
 * @example
 *
 * const conditional = '';
 * buildString(['This is', conditional && `not ${conditional}`, ' included.']);
 * // => 'This is included'
 */
export function buildString(parts) {
  return parts.filter((part) => Boolean(part) || part === 0).join('');
}

/**
 * Change the first character of a string to lowercase.
 *
 * @param {string} str - String to change.
 * @returns {string}
 */
export function lowerFirst(str) {
  return str[0].toLowerCase() + str.slice(1);
}

/**
 * Change the first character of a string to uppercase.
 *
 * @param {string} str - String to change.
 * @returns {string}
 */
export function upperFirst(str) {
  return str[0].toUpperCase() + str.slice(1);
}

/**
 * Callback for string.replace().
 *
 * @ignore
 * @see camelCase()
 * @param {string} match - The matched substring.
 * @param {string} group1 - The first captured group.
 * @returns {string}
 */
function camelCaseReplacer(match, group1) {
  return group1.toUpperCase();
}

/**
 * Convert a string to camelCase.
 *
 * Handles kebab-case, PascalCase, snake_case and spaced words.
 *
 * @param {string} str - String to convert.
 * @returns {string}
 */
export function camelCase(str) {
  return lowerFirst(str).replaceAll(
    /(?!^)[\s_-]+([a-zA-Z\d])/g,
    camelCaseReplacer,
  );
}

/**
 * Convert a string to PascalCase.
 *
 * Handles kebab-case, camelCase, snake_case and spaced words.
 *
 * @param {string} str - String to convert.
 * @returns {string}
 */
export function pascalCase(str) {
  return upperFirst(camelCase(str));
}

/**
 * Convert a string to kebab-case.
 *
 * Handles camelCase, PascalCase, snake_case and spaced words.
 *
 * @param {string} str - String to convert.
 * @returns {string}
 */
export function kebabCase(str) {
  // Start by converting camelCase, then convert other separators.
  return str
    .replaceAll(/([a-zA-Z])(?=[A-Z])/g, '$1-')
    .replaceAll(/(?!^)[\s_-]+([a-zA-Z\d])/g, '-$1')
    .toLowerCase();
}

/**
 * Convert a string to snake_case.
 *
 * Handles camelCase, PascalCase, kebab-case and spaced words.
 *
 * @param {string} str - String to convert.
 * @returns {string}
 */
export function snakeCase(str) {
  return kebabCase(str).replaceAll(/(?!^)-+([a-zA-Z\d])/g, '_$1');
}

const FORMAT_TAG_REGEX = /\{\{\s*[\w-]+\s*\}\}/gm;
const FORMAT_TAG_KEY_REGEX = /(^\{\{\s*|\s*\}\}$)/g;

/**
 * Get mustache template tags from a string.
 *
 * @ignore
 * @param {string} template - Mustache template string.
 * @returns {Array} Raw mustache tags.
 * @example
 *
 * getFormatTags('Have {{num}} drinks, {{name}}');
 * // => ['{{num}}', '{{drinks}}']
 */
function getFormatTags(template) {
  return template.match(FORMAT_TAG_REGEX) || [];
}

/**
 * Get mustache template tag keys from a string.
 *
 *   'Have {{num}} drinks, {{name}}' => ['num', 'drinks']
 *
 * @ignore
 * @param {string} template - Mustache template string.
 * @returns {Array} Clean mustache tag names.
 * @example
 *
 * getFormatTagKeys('Have {{num}} drinks, {{name}}');
 * // => ['num', 'drinks']
 */
function getFormatTagKeys(template) {
  return getFormatTags(template).map((match) =>
    match.replaceAll(FORMAT_TAG_KEY_REGEX, ''),
  );
}

/**
 * Format a mustache-ish template string.
 *
 * @param {string} template - Mustache template string.
 * @param {object} templateTags - Map of tag name > value pairs. Values can
 *   be numbers, strings or functions. For functions, the return value is used.
 * @throws {TypeError} If the value for a type is invalid.
 * @returns {string}
 * @example
 *
 * format('{{name}} wants {{min}} to {{max}} drinks', {
 *   name: 'Joe',
 *   min: 1,
 *   max: () => 1 + 2,
 * });
 * // => 'Joe wants 1 to 3 drinks'
 */
export function format(template, templateTags) {
  const formatTags = getFormatTags(template);
  const formatKeys = getFormatTagKeys(template);

  return formatTags.reduce((result, formatTag, i) => {
    const tagKey = formatKeys[i];
    const tagValue = templateTags[tagKey];

    if (!isString(tagValue) && !isNumber(tagValue) && !isFunc(tagValue)) {
      throw new TypeError(
        `Unexpected value \`${tagValue}\` for \`{{${tagKey}}}\` in "${template}"`,
      );
    }

    const value = isFunc(tagValue) ? tagValue() : tagValue;
    return result.replaceAll(new RegExp(formatTag, 'g'), value);
  }, template);
}
